import { useCallback, useEffect } from 'react';

import { AnalyticsEvent, track } from '@bonusly/analytics';

import { useBizyExternalOpenStore, type BizyExternalOpenContext } from '../store/use-bizy-external-open-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

const BIZY_OPEN_EVENT = 'bizy:open';

export interface BizyOpenEventDetail {
	source: string;
	pagePath: string;
	pageContext: string;
	starterMessage?: string;
}

declare global {
	interface WindowEventMap {
		[BIZY_OPEN_EVENT]: CustomEvent<BizyOpenEventDetail>;
	}
}

export function useBizyOpenListener() {
	const openPanel = useBizySidePanelStore((state) => state.open);
	const showChat = useBizySidePanelStore((state) => state.showChat);
	const setPendingRequest = useBizyExternalOpenStore((state) => state.setPendingRequest);

	const handleBizyOpen = useCallback(
		(event: CustomEvent<BizyOpenEventDetail>) => {
			const detail = event.detail;

			if (detail.source === 'feature_request') {
				track(AnalyticsEvent.FeatureRequest.ButtonClicked, {
					pagePath: detail.pagePath,
					pageContext: detail.pageContext,
				});
			} else {
				track(AnalyticsEvent.BizyPromptButton.Clicked, {
					source: detail.source,
					pagePath: detail.pagePath,
					pageContext: detail.pageContext,
				});
			}

			if (detail.starterMessage) {
				const requestContext: BizyExternalOpenContext = {
					source: detail.source,
					pagePath: detail.pagePath,
					pageContext: detail.pageContext,
					starterMessage: detail.starterMessage,
				};

				setPendingRequest(requestContext);
			}

			showChat();
			openPanel(detail.source);
		},
		[openPanel, showChat, setPendingRequest]
	);

	useEffect(() => {
		window.addEventListener(BIZY_OPEN_EVENT, handleBizyOpen);

		return () => {
			window.removeEventListener(BIZY_OPEN_EVENT, handleBizyOpen);
		};
	}, [handleBizyOpen]);
}

export function dispatchBizyOpenEvent(detail: BizyOpenEventDetail): void {
	const event = new CustomEvent(BIZY_OPEN_EVENT, { detail });
	window.dispatchEvent(event);
}
